/**
 * Your turn: choose dice to keep, use your card's power, then roll, end the turn, or attack.
 *
 * Shows only on your turn, inside your big seat at the bottom of the table (see `GameTable`),
 * under your card. Targets are picked by clicking the other seats; the table highlights any card
 * your kept dice can destroy.
 *
 * Dice: one row, in the order they fell, each in its own column (outlined places for missing
 * dice). Tapping a die holds it (a 🔒 badge on its top-right corner); tapping again lets it go. No hold mat. The
 * buttons sit under the dice.
 *
 * Kept dice and power changes are owned by `GameTable` (the table needs them for the
 * highlight). The rule helpers from `@kadice/shared` are hints only; the server re-validates.
 *
 * Changing a die with a power keeps it automatically; changing it back releases it (dice saved
 * on earlier turns stay kept). Dice saved on earlier turns are locked — no arrows, only
 * discard — unless the pool is five of a kind. One-die powers (Three of a Kind, ≤10, ≥25): the first changed die
 * (held or not) is the committed change — only that die keeps its arrows and flame, to undo it.
 *
 * A fresh roll plays its animation (`rolling.ts`) before the dice can be used; nothing can be
 * done while it plays, while anything else animates on the table (`locked`), or while play is
 * paused for a Beginner Mode player's tutorial.
 *
 * Order: the dice row, centred (its round ALL button, hold every rolled die, is off for now: a
 * TODO), then the buttons, then the power hints. In the big seat its parts join the seat's grid (`.turn` is
 * `display: contents`): the buttons stacked beside your card under your name and power, the
 * dice and hints under the card. Your power shows there too (`GameTable`), not here.
 *
 * Beginner mode adds a HINT button at the front of the button row: it lights the dice worth
 * keeping (`usefulDice`) until pressed again or the dice change.
 *
 * Beginner mode: End Turn while the dice on the table (held or not) could destroy a card asks
 * first, in a see-through box (`killWarn`). Those cards burn meanwhile, as they do whenever
 * the dice can destroy them (`GameTable` `couldKill`, `Seat killFire`).
 *
 * Beginner mode hides every button (ALL, HINT, the action row) while a tutorial screen is up
 * or still to come (`hideControls`); their space is kept so nothing moves when they appear.
 *
 * Five of a kind: the moment your dice match, on the mat and in the tray together, every die
 * goes wild. A natural roll does it on the server; a power change that assembles it is sent at
 * once (`game:wild`), and the server answers with the dice back in the tray, all wild
 * (`turn.wild`): every die burns and takes arrows, held ones too.
 *
 * Allow Bot Collusion: after rolling, a Deal button opens the deal offer (`Deals.tsx`); while
 * your deal waits on an answer every button is off and a line says who you're waiting on.
 * Attacking a player you promised to spare asks first (`spared`).
 *
 * Debug mode adds a 5x button: this roll counts as five of a kind (any die to anything). The
 * panel remounts per roll, so it switches off on the next roll.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  DICE_COUNT,
  MAX_SAVED_DICE,
  DIE_FACES,
  applyMods,
  canModifyDie,
  diceRule,
  isFiveOfAKind,
  t,
  tList,
  tn,
  type CardDef,
  type DeckColor,
  type DieMod,
  type PublicPlayer,
  type TurnView,
} from '@kadice/shared';
import { usefulDice } from '../beginner';
import { rollingDice, type RollFx } from '../rolling';
import { useMotionArm } from '../dice3d/DiceTray';
import type { GameClient } from '../useGameClient';
import { Pips } from './Dice';

interface Props {
  client: GameClient;
  turn: TurnView;
  card: CardDef | undefined;
  keep: number[];
  mods: DieMod[];
  onDiceChange: (keep: number[], mods: DieMod[]) => void;
  /** Players currently selected as attack targets. */
  targets: PublicPlayer[];
  /** Free kills your kept dice currently earn (see `freeKills`). */
  free: number;
  /** Debug mode: show the 5x button. */
  debug: boolean;
  /** Beginner mode: the HINT button. */
  tips: boolean;
  /** Your roll's animation, while it plays. */
  rollFx: RollFx | null;
  /** Play is paused for someone's Beginner Mode tutorial. */
  paused: boolean;
  /** Something is animating on the table: no input (see `GameTable`). */
  locked: boolean;
  /** Beginner Mode tutorials are up or still to come: the buttons are hidden (their space kept). */
  hideControls: boolean;
  /** Beginner Mode: the dice on the table could destroy a card, so End Turn asks first. */
  killWarn: boolean;
  /** Allow Bot Collusion: open the deal offer (the Deal button); absent when you can't offer one now. */
  onDeal?: () => void;
  /** Your deal is waiting on this player's answer (your turn is on hold), with seconds left (null: a bot's). */
  dealWait: { name: string; seconds: number | null } | null;
  /** Players you promised to spare: attacking one asks first. */
  spared: readonly PublicPlayer['id'][];
}

type Rule = ReturnType<typeof diceRule>;

/**
 * A die the power can change right now (the die burns): any die when the dice are wild, else a
 * die rolled this roll that the power can set to some other face. Also used for other players'
 * dice in the big seat (`GameTable`'s `BigDice`).
 */
export function canChangeDie(rule: Rule, original: number, i: number, firstFresh: number): boolean {
  return (
    rule.kind !== 'none' &&
    (rule.kind === 'any' || i >= firstFresh) &&
    Array.from({ length: DIE_FACES }, (_, v) => v + 1).some((v) => canModifyDie(rule, original, v))
  );
}

/** The flame behind a burning die, in the deck's colour. */
export function fireSrc(color: DeckColor): string {
  return `/img/anim/fire/${color === 'gray' ? 'grey' : color}.gif`;
}

export function TurnPanel({
  client,
  turn,
  card,
  keep,
  mods,
  onDiceChange,
  targets,
  free,
  debug,
  tips,
  rollFx,
  paused,
  locked,
  hideControls,
  killWarn,
  onDeal,
  dealWait,
  spared,
}: Props) {
  const { request } = client;
  const arm = useMotionArm();
  const [sending, setSending] = useState(false);
  const [fiveX, setFiveX] = useState(false);
  const [hinting, setHinting] = useState(false);
  const [warning, setWarning] = useState(false);
  // Beginner Mode: End Round (all five dice kept) asks first.
  const [fiveWarning, setFiveWarning] = useState(false);
  // Attacking a player you promised to spare asks first.
  const [breakWarning, setBreakWarning] = useState(false);
  // Ending the turn with an attack target picked asks first (an in-app box: the browser's own
  // confirm() gets suppressed or dismissed on phones, which made End Turn take many presses).
  const [endWarning, setEndWarning] = useState(false);
  const debug5x = debug && fiveX;

  const wild = debug5x || turn.wild;
  const rule = diceRule(turn.pool, card?.power, wild);
  const dice = applyMods(turn.pool, mods);
  const firstFresh = turn.pool.length - turn.rolled;
  const shown = rollingDice(dice, firstFresh, rollFx);
  const busy = sending || paused || locked || shown.rolling.length > 0 || dealWait !== null;
  const view = client.view;
  const power = card?.power ?? null;
  const useful = useMemo(
    () => (hinting && view ? usefulDice(view, client.catalog, turn, power) : null),
    [hinting, view, client.catalog, turn, power],
  );

  // One-die powers: the first changed die is the committed change (held or not); only it keeps
  // its arrows, until it is changed back.
  const committed = rule.kind === 'nudge' ? mods[0] : undefined;
  const change = (nextKeep: number[], nextMods: DieMod[]) => {
    onDiceChange(nextKeep, rule.kind === 'nudge' ? nextMods.slice(0, 1) : nextMods);
  };

  // TODO: ALL (hold every rolled die) is off for now; decide where it goes, or drop it.
  // const holdAll = () =>
  //   change(
  //     Array.from({ length: turn.pool.length }, (_, i) => i),
  //     mods,
  //   );

  const toggleKeep = (i: number) =>
    change(keep.includes(i) ? keep.filter((x) => x !== i) : [...keep, i].sort((a, b) => a - b), mods);

  // Changing a die never moves it; holding it is always a separate click. A change that makes
  // five of a kind turns every die wild at once.
  const setDie = (index: number, value: number) => {
    const others = mods.filter((m) => m.index !== index);
    const next = value === turn.pool[index] ? others : [...others, { index, value }];
    const sent = rule.kind === 'nudge' ? next.slice(0, 1) : next;
    change(keep, sent);
    if (!wild && turn.hasRolled && isFiveOfAKind(applyMods(turn.pool, sent))) {
      void send(() => request('game:wild', { mods: sent }));
    }
  };

  const send = async (action: () => Promise<unknown>) => {
    setSending(true);
    await action();
    setSending(false);
  };

  // Motion dice: the tray opens with your dice in your hand; the roll goes when you throw them.
  const roll = () => {
    if (!arm) return void send(() => request('game:roll', { keep }));
    arm({
      count: DICE_COUNT - keep.length,
      held: keep.map((i) => turn.pool[i]!),
      send: async (toss) => {
        let ok = false;
        await send(async () => {
          ok = (await request('game:roll', { keep, toss })) !== undefined;
        });
        return ok;
      },
    });
  };

  const endNow = () => void send(() => request('game:endTurn', { keep, mods, debug5x }));
  // Keeping all five dice can't save them: ending now ends your round (the server does the same).
  const endsRound = !turn.bonus && keep.length > MAX_SAVED_DICE;
  const endTurn = () => {
    if (killWarn) return setWarning(true);
    if (endsRound && tips) return setFiveWarning(true);
    if (targets.length > 0) return setEndWarning(true);
    endNow();
  };

  const attackNow = () =>
    void send(() => request('game:attack', { targets: targets.map((t) => t.id), keep, mods, debug5x }));
  const breaking = targets.filter((p) => spared.includes(p.id));
  const attack = () => (breaking.length > 0 ? setBreakWarning(true) : attackNow());

  // Turning 5x off drops changes it allowed.
  const toggleFiveX = () => {
    if (fiveX) onDiceChange(keep, []);
    setFiveX(!fiveX);
  };

  return (
    <div className={`turn${hideControls ? ' controls-hidden' : ''}`}>
      {warning &&
        createPortal(
          <div className="modal-backdrop kill-warn-backdrop">
            <div className="modal kill-warn-box" role="alertdialog" aria-modal="true">
              <h2>{t('turn.killWarnTitle')}</h2>
              <p>{t('turn.killWarnBody')}</p>
              <div className="row actions">
                <button
                  onClick={() => {
                    setWarning(false);
                    if (endsRound && tips) setFiveWarning(true);
                    else endNow();
                  }}
                >
                  {t('turn.killWarnEnd')}
                </button>
                <button className="primary" autoFocus onClick={() => setWarning(false)}>
                  {t('turn.killWarnBack')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {fiveWarning &&
        createPortal(
          <div className="modal-backdrop kill-warn-backdrop">
            <div className="modal kill-warn-box" role="alertdialog" aria-modal="true">
              <h2>{t('turn.fiveWarnTitle')}</h2>
              <p>{t('turn.fiveWarnBody', { max: MAX_SAVED_DICE })}</p>
              <div className="row actions">
                <button
                  onClick={() => {
                    setFiveWarning(false);
                    endNow();
                  }}
                >
                  {t('turn.fiveWarnEnd')}
                </button>
                <button className="primary" autoFocus onClick={() => setFiveWarning(false)}>
                  {t('turn.killWarnBack')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {endWarning &&
        createPortal(
          <div className="modal-backdrop kill-warn-backdrop">
            <div className="modal kill-warn-box" role="alertdialog" aria-modal="true">
              <h2>{t('turn.confirmEndTitle')}</h2>
              <p>{t('turn.confirmEnd')}</p>
              <div className="row actions">
                <button
                  onClick={() => {
                    setEndWarning(false);
                    endNow();
                  }}
                >
                  {t('turn.killWarnEnd')}
                </button>
                <button className="primary" autoFocus onClick={() => setEndWarning(false)}>
                  {t('turn.killWarnBack')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {breakWarning &&
        createPortal(
          <div className="modal-backdrop kill-warn-backdrop">
            <div className="modal kill-warn-box" role="alertdialog" aria-modal="true">
              <h2>{t('deal.breakTitle')}</h2>
              <p>{t('deal.breakBody', { names: tList(breaking.map((p) => p.name)) })}</p>
              <div className="row actions">
                <button
                  onClick={() => {
                    setBreakWarning(false);
                    attackNow();
                  }}
                >
                  {t('deal.breakGo')}
                </button>
                <button className="primary" autoFocus onClick={() => setBreakWarning(false)}>
                  {t('turn.killWarnBack')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      <DiceRow
        color={client.view?.players.find((p) => p.id === turn.playerId)?.color ?? null}
        dice={shown.values}
        rolling={shown.rolling}
        pool={turn.pool}
        rule={rule}
        wild={wild}
        arrows={(i) =>
          rule.kind !== 'none' && (rule.kind === 'any' || i >= firstFresh) && (!committed || committed.index === i)
        }
        keep={keep}
        firstFresh={firstFresh}
        onToggleKeep={toggleKeep}
        onSetDie={setDie}
        glow={useful ?? []}
        // TODO: ALL is off for now (see holdAll above).
        // after={
        //   <button
        //     className="hold-all"
        //     disabled={busy || !turn.hasRolled || keep.length === turn.pool.length}
        //     onClick={holdAll}
        //     title={t('turn.holdAllTitle')}
        //     aria-label={t('turn.holdAllTitle')}
        //   >
        //     {t('turn.holdAll')}
        //   </button>
        // }
      />

      <div className="row actions">
        {tips && (
          <button
            className="hint-btn"
            disabled={busy}
            aria-pressed={hinting}
            title={t('turn.hintTitle')}
            onClick={() => setHinting(!hinting)}
          >
            {t('turn.hint')}
          </button>
        )}
        {turn.hasRolled || turn.bonus ? (
          <button disabled={busy} onClick={endTurn}>
            {turn.bonus ? t('turn.end') : endsRound ? t('turn.endRound') : t('turn.endKeep', { n: keep.length })}
          </button>
        ) : null}
        {turn.hasRolled ? null : (
          <button className="primary" disabled={busy} onClick={roll}>
            {tn('turn.roll', DICE_COUNT - keep.length)}
          </button>
        )}
        <button
          className="danger attack-btn"
          disabled={busy || targets.length === 0 || keep.length === 0}
          title={targets.length === 0 ? t('turn.attackHint') : undefined}
          onClick={attack}
        >
          {targets.length === 0 ? t('turn.tapEnemy') : tn('turn.attacking', targets.length)}
        </button>
        {onDeal && (
          <button className="deal-btn" disabled={busy} title={t('deal.buttonTitle')} onClick={onDeal}>
            {t('deal.button')}
          </button>
        )}
        {debug && turn.hasRolled && (
          <button
            className={debug5x ? 'primary' : undefined}
            aria-pressed={debug5x}
            title={t('turn.debug5xHint')}
            onClick={toggleFiveX}
          >
            {t('turn.debug5x')}
          </button>
        )}
      </div>
      {dealWait && (
        <p className="power-hint deal-wait">
          {t('deal.waiting', { name: dealWait.name })}
          {dealWait.seconds !== null && ` ${t('deal.seconds', { n: dealWait.seconds })}`}
        </p>
      )}
      {useful?.length === 0 && <p className="power-hint">{t('turn.hintNone')}</p>}
      {rule.kind === 'any' && <p className="power-hint">{t('turn.fiveOfAKind')}</p>}
      {free > 0 && (
        <p className="power-hint">{free === Infinity ? t('turn.freeKillsAny') : tn('turn.freeKills', free)}</p>
      )}
    </div>
  );
}

/**
 * Your dice in one row, in the order they fell (die `i` in column `i`; missing dice keep their
 * place as faint outlines). Tap a die to hold it: a 🔒 shows under it; tap again to let it go.
 * Each die has ▲ above and ▼ below when a power allows it. Hidden arrows and locks keep their
 * space, so the dice never shift. `after`, if any, sits at the row's right end.
 */
function DiceRow({
  color,
  dice,
  rolling,
  pool,
  rule,
  wild,
  arrows,
  keep,
  firstFresh,
  onToggleKeep,
  onSetDie,
  glow,
  after,
}: {
  /** Deck colour: the flame behind each modifiable die. */
  color: DeckColor | null;
  /** As shown: mid-roll dice show their flashing face (0 = not rolled yet, shown as an empty place). */
  dice: number[];
  /** Indexes still rolling: no flames, arrows or clicks yet. */
  rolling: readonly number[];
  pool: readonly number[];
  rule: Rule;
  /** Five of a kind: every die is wild and burns, held ones too. */
  wild: boolean;
  /** Show ▲/▼ on this die (a power applies, the die isn't locked from an earlier turn, and no other die holds a committed one-die change). */
  arrows: (i: number) => boolean;
  keep: number[];
  firstFresh: number;
  onToggleKeep: (i: number) => void;
  onSetDie: (i: number, value: number) => void;
  /** Dice to light up (the HINT). */
  glow: readonly number[];
  after?: ReactNode;
}) {
  // A step is allowed if it lands back on the rolled value or on a value the power permits.
  const canStep = (original: number, target: number) => target === original || canModifyDie(rule, original, target);
  const lock = (
    <span className="die-lock" aria-hidden>
      🔒
    </span>
  );

  const slots = Array.from({ length: DICE_COUNT }, (_, i) => {
    const value = dice[i];
    // 0: a rolled die whose animation hasn't reached it yet; its place stays empty.
    if (value === undefined || value === 0) {
      // Same parts as a die, so the row keeps its size; the place shows as a faint outline.
      return (
        <div key={`empty-${i}`} className="die-slot empty" aria-hidden>
          <Arrow dir="up" enabled={false} onClick={() => {}} />
          <span className="die ghost" />
          <Arrow dir="down" enabled={false} onClick={() => {}} />
        </div>
      );
    }
    const original = pool[i]!;
    const held = keep.includes(i);
    if (rolling.includes(i)) {
      return (
        <div key={i} className="die-slot">
          <Arrow dir="up" enabled={false} onClick={() => {}} />
          <div className="die-fx">
            <span className="die rolling" role="img" aria-label={t('dice.rolling')}>
              <Pips value={value} rolling />
            </span>
          </div>
          <Arrow dir="down" enabled={false} onClick={() => {}} />
        </div>
      );
    }
    const modded = value !== original;
    // A die the power can change right now burns in the deck's colour. Held dice are out of play
    // and don't burn, unless the dice are wild.
    const changeable = arrows(i) && canChangeDie(rule, original, i, firstFresh);
    const fire = color !== null && (!held || wild) && changeable;
    return (
      <div key={i} className={`die-slot${held ? ' held' : ''}`}>
        <Arrow dir="up" enabled={arrows(i) && canStep(original, value + 1)} onClick={() => onSetDie(i, value + 1)} />
        <div className="die-fx">
          {fire && <img className="fire" src={fireSrc(color)} alt="" />}
          <button
            className={`die${modded ? ' modded' : ''}${i >= firstFresh ? ' fresh' : ''}${glow.includes(i) ? ' hint' : ''}`}
            onClick={() => onToggleKeep(i)}
            aria-pressed={held}
            aria-label={t(held ? 'dice.heldLabel' : 'dice.rolledLabel', { value })}
            title={modded ? t('dice.changedFrom', { value: original }) : undefined}
          >
            <Pips value={value} />
          </button>
          {held && lock}
        </div>
        <Arrow dir="down" enabled={arrows(i) && canStep(original, value - 1)} onClick={() => onSetDie(i, value - 1)} />
      </div>
    );
  });

  return (
    // No power to change dice this roll: no ▲/▼ rows (their room goes to the table).
    <div
      className={`dice-row${color ? ' on-fire' : ''}${wild ? ' wild' : ''}${rule.kind === 'none' ? ' no-arrows' : ''}`}
    >
      <div className="dice big dice-line">{slots}</div>
      {after && <span className="dice-row-end">{after}</span>}
    </div>
  );
}

/** A disallowed arrow is hidden but keeps its space so the dice stay aligned. */
function Arrow({ dir, enabled, onClick }: { dir: 'up' | 'down'; enabled: boolean; onClick: () => void }) {
  return (
    <button
      className={`die-arrow ${dir}`}
      style={enabled ? undefined : { visibility: 'hidden' }}
      disabled={!enabled}
      onClick={onClick}
      aria-label={t(dir === 'up' ? 'dice.raise' : 'dice.lower')}
    >
      {dir === 'up' ? '▲' : '▼'}
    </button>
  );
}
